import React, { useState, useEffect, useCallback } from 'react'
import ApiAnalytics from '../../api/ApiAnalytics'
import toastr from 'toastr'
import './PrimaryMetricsCards.css'

/**
 * PrimaryMetricsCards Component
 *
 * Displays 4 key health metrics at the top of analytics dashboard:
 * - Matches Scheduled (primary/emphasized)
 * - Scores Entered
 * - League Registrations
 * - New Users
 *
 * Features:
 * - Time window selector (24h, 7d, 30d)
 * - Delta vs previous period with up/down arrows
 * - Responsive layout (stacks on mobile)
 * - Visual hierarchy (Matches emphasized)
 */
function PrimaryMetricsCards() {
  const [timeWindow, setTimeWindow] = useState('7d')
  const [metrics, setMetrics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isPaused, setIsPaused] = useState(() => {
    try {
      const saved = localStorage.getItem('metricsAutoRefreshPaused')
      return saved === 'true'
    } catch {
      return false
    }
  })

  // Persist pause state to localStorage
  const togglePause = () => {
    setIsPaused((prev) => {
      const newValue = !prev
      try {
        localStorage.setItem('metricsAutoRefreshPaused', newValue)
      } catch {
        // Ignore localStorage errors
      }
      return newValue
    })
  }

  // Load metrics function
  const loadMetrics = useCallback(async () => {
    try {
      setLoading(true)
      setMetrics(null) // Clear old data while loading
      const data = await ApiAnalytics.getPrimaryMetrics(timeWindow)
      setMetrics(data)
    } catch (error) {
      console.error('Failed to load primary metrics:', error)
      toastr.error('Failed to load primary metrics')
    } finally {
      setLoading(false)
    }
  }, [timeWindow])

  // Load metrics whenever time window changes
  useEffect(() => {
    // Initial load
    loadMetrics()

    // Auto-refresh every 5 seconds (only if not paused)
    let refreshInterval
    if (!isPaused) {
      refreshInterval = setInterval(loadMetrics, 5000)
    }

    // Cleanup interval on unmount or timeWindow/isPaused change
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }
    }
  }, [timeWindow, isPaused, loadMetrics])

  const formatDelta = (delta) => {
    if (delta === 0) return '—'
    const sign = delta > 0 ? '↑' : '↓'
    return `${sign} ${Math.abs(delta)}%`
  }

  const getDeltaClassName = (delta) => {
    if (delta === 0) return 'metric-delta-neutral'
    return delta > 0 ? 'metric-delta-positive' : 'metric-delta-negative'
  }

  const getDeltaAriaLabel = (metricName, delta) => {
    if (delta === 0)
      return `${metricName}: no change compared to previous period`
    const direction = delta > 0 ? 'up' : 'down'
    return `${metricName}: ${direction} ${Math.abs(delta)}% compared to previous period`
  }

  const getTimeWindowLabel = (window) => {
    switch (window) {
      case '24h':
        return 'Last 24 hours'
      case '7d':
        return 'Last 7 days'
      case '30d':
        return 'Last 30 days'
      default:
        return ''
    }
  }

  if (loading) {
    return (
      <div className="primary-metrics-section mb-4">
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading metrics...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="primary-metrics-section mb-4">
      {/* Time Window Selector */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="row align-items-center">
            <div className="col-12 col-md-5">
              <h5 className="mb-1">Primary Health Metrics</h5>
              <small className="text-muted">
                {isPaused ? (
                  <>
                    <span className="text-warning">⏸ Paused</span> ·{' '}
                    {getTimeWindowLabel(timeWindow)}
                  </>
                ) : (
                  <>5-second heartbeat · {getTimeWindowLabel(timeWindow)}</>
                )}
              </small>
            </div>
            <div className="col-12 col-md-3 mt-2 mt-md-0">
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className={`btn ${isPaused ? 'btn-success' : 'btn-secondary'} flex-fill`}
                  onClick={togglePause}
                  aria-label={
                    isPaused ? 'Resume auto-refresh' : 'Pause auto-refresh'
                  }
                >
                  {isPaused ? '▶ Resume' : '⏸ Pause'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary flex-fill"
                  onClick={loadMetrics}
                  aria-label="Refresh metrics now"
                  disabled={loading}
                >
                  ↻ Refresh
                </button>
              </div>
            </div>
            <div className="col-12 col-md-4 mt-2 mt-md-0">
              <div
                className="btn-group w-100"
                role="group"
                aria-label="Time window selector"
              >
                <button
                  type="button"
                  className={`btn ${timeWindow === '24h' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setTimeWindow('24h')}
                >
                  Last 24h
                </button>
                <button
                  type="button"
                  className={`btn ${timeWindow === '7d' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setTimeWindow('7d')}
                >
                  Last 7d
                </button>
                <button
                  type="button"
                  className={`btn ${timeWindow === '30d' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setTimeWindow('30d')}
                >
                  Last 30d
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="row g-3">
        {/* Matches Scheduled - Primary/Emphasized */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div
            className="card h-100 metric-card metric-card-primary"
            role="region"
            aria-label="Matches scheduled metric"
          >
            <div className="card-body text-center">
              <div
                className="metric-number metric-number-primary"
                aria-label="Current value"
              >
                {metrics?.matchesScheduled?.current ?? 0}
              </div>
              <div className="metric-label">Matches Scheduled</div>
              <div
                className={`metric-delta ${getDeltaClassName(metrics?.matchesScheduled?.delta ?? 0)}`}
                aria-label={getDeltaAriaLabel(
                  'Matches scheduled',
                  metrics?.matchesScheduled?.delta ?? 0,
                )}
              >
                {formatDelta(metrics?.matchesScheduled?.delta ?? 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Scores Entered */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div
            className="card h-100 metric-card metric-card-secondary"
            role="region"
            aria-label="Scores entered metric"
          >
            <div className="card-body text-center">
              <div className="metric-number" aria-label="Current value">
                {metrics?.scoresEntered?.current ?? 0}
              </div>
              <div className="metric-label">Scores Entered</div>
              <div
                className={`metric-delta ${getDeltaClassName(metrics?.scoresEntered?.delta ?? 0)}`}
                aria-label={getDeltaAriaLabel(
                  'Scores entered',
                  metrics?.scoresEntered?.delta ?? 0,
                )}
              >
                {formatDelta(metrics?.scoresEntered?.delta ?? 0)}
              </div>
            </div>
          </div>
        </div>

        {/* League Registrations */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div
            className="card h-100 metric-card"
            role="region"
            aria-label="League registrations metric"
          >
            <div className="card-body text-center">
              <div className="metric-number" aria-label="Current value">
                {metrics?.leagueRegistrations?.current ?? 0}
              </div>
              <div className="metric-label">League Registrations</div>
              <div
                className={`metric-delta ${getDeltaClassName(metrics?.leagueRegistrations?.delta ?? 0)}`}
                aria-label={getDeltaAriaLabel(
                  'League registrations',
                  metrics?.leagueRegistrations?.delta ?? 0,
                )}
              >
                {formatDelta(metrics?.leagueRegistrations?.delta ?? 0)}
              </div>
            </div>
          </div>
        </div>

        {/* New Users */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div
            className="card h-100 metric-card"
            role="region"
            aria-label="New users metric"
          >
            <div className="card-body text-center">
              <div className="metric-number" aria-label="Current value">
                {metrics?.newUsers?.current ?? 0}
              </div>
              <div className="metric-label">New Users</div>
              <div
                className={`metric-delta ${getDeltaClassName(metrics?.newUsers?.delta ?? 0)}`}
                aria-label={getDeltaAriaLabel(
                  'New users',
                  metrics?.newUsers?.delta ?? 0,
                )}
              >
                {formatDelta(metrics?.newUsers?.delta ?? 0)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrimaryMetricsCards
